import { useState, useEffect, useRef } from 'react';
import { Languages, Plus, Save, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { DeclarationInformation } from './DeclarationSummary/DeclarationInformation';
import { ItemInformation } from './DeclarationSummary/ItemInformation';
import { DocumentsTable } from './DeclarationSummary/DocumentsTable';
import { PackagesTable } from './DeclarationSummary/PackagesTable';
import { ValidationErrors } from './DeclarationSummary/ValidationErrors';
import { DeclarationDocumentsTable } from './DeclarationSummary/DeclarationDocumentsTable';
import { useDeclarationSummary } from '../../hooks/useDeclarationSummary';
import { getDeclarationSummaryTranslation, getMainTranslation } from '../../hooks/useLanguage';

export const DeclarationSummary = ({ jsonData, isSummaryExpanded, selectedOption }) => {
  const [activeDeclarationIndex, setActiveDeclarationIndex] = useState(-1); // -1 means "All"
  const [activeItemIndices, setActiveItemIndices] = useState({}); // Object mapping declarationIndex to itemIndex
  const {
    isSpanish,
    setIsSpanish,
    editableData,
    setEditableData,
    isDirty,
    setIsDirty,
    validationErrors,
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  } = useDeclarationSummary(jsonData);

  const handleSaveClick = () => {
    const result = saveChanges();
    if (result?.success === false) {
      toast.error(getMainTranslation('fixAllRequiredBeforeSave', isSpanish));
    } else {
      toast.success(getMainTranslation('changesSaved', isSpanish));
    }
  };

  const handleCancelClick = () => {
    cancelChanges();
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };

  // Reset active declaration index if it's out of bounds (but keep -1 for "All")
  useEffect(() => {
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      // If no data, reset to "All"
      if (activeDeclarationIndex !== -1) {
        setActiveDeclarationIndex(-1);
      }
      return;
    }
    
    // Validate activeDeclarationIndex is within bounds
    if (activeDeclarationIndex >= editableData.length && activeDeclarationIndex !== -1) {
      setActiveDeclarationIndex(-1); // Reset to "All" if out of bounds
    }
  }, [editableData, activeDeclarationIndex]);

  // Helper function to get active item index for a declaration
  const getActiveItemIndex = (declarationIndex) => {
    return activeItemIndices[declarationIndex] ?? -1;
  };

  // Helper function to set active item index for a declaration
  const setActiveItemIndex = (declarationIndex, itemIndex) => {
    setActiveItemIndices(prev => ({
      ...prev,
      [declarationIndex]: itemIndex
    }));
  };

  // State for declaration-level documentos toggle (per declaration)
  const [hasDeclarationDocuments, setHasDeclarationDocuments] = useState({});
  const [removeDuplicates, setRemoveDuplicates] = useState({});
  const prevPartidasDocumentosRefs = useRef({});
  const isManualEditRefs = useRef({});

  // Function to aggregate documentos from all partidas
  const aggregateDocumentosFromPartidas = (declaration, removeDups = false) => {
    if (!declaration.partidas || !Array.isArray(declaration.partidas)) {
      return [];
    }
    
    const allDocumentos = [];
    declaration.partidas.forEach((partida) => {
      if (partida.documentos && Array.isArray(partida.documentos)) {
        partida.documentos.forEach((doc) => {
          if (doc && typeof doc === 'object') {
            allDocumentos.push({ ...doc }); // Deep copy
          }
        });
      }
    });
    
    if (removeDups) {
      // Remove duplicates based on codigo_documento, tipo, numero, and fecha
      const seen = new Set();
      return allDocumentos.filter((doc) => {
        const key = `${doc.codigo_documento || ''}_${doc.tipo || ''}_${doc.numero || ''}_${doc.fecha || ''}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    }
    
    return allDocumentos;
  };

  // Handle toggle for declaration-level documentos
  const handleToggleDeclarationDocuments = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const updatedData = JSON.parse(JSON.stringify(editableData));
    const currentState = hasDeclarationDocuments[declarationIndex] || false;
    const newState = !currentState;
    
    if (newState) {
      // Enable: aggregate documentos from all partidas
      const removeDups = removeDuplicates[declarationIndex] || false;
      const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
      updatedData[declarationIndex].documentos = aggregated;
      // Update the ref signature to prevent immediate re-sync
      if (declaration.partidas) {
        const signature = declaration.partidas
          .map(p => p.documentos ? JSON.stringify(p.documentos) : '[]')
          .join('|');
        prevPartidasDocumentosRefs.current[declarationIndex] = signature;
      }
    } else {
      // Disable: clear declaration-level documentos
      updatedData[declarationIndex].documentos = [];
    }
    
    setEditableData(updatedData);
    setIsDirty(true);
    setHasDeclarationDocuments(prev => ({ ...prev, [declarationIndex]: newState }));
    isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
  };

  // Handle remove duplicates toggle
  const handleToggleRemoveDuplicates = (declarationIndex) => {
    const declaration = editableData[declarationIndex];
    if (!declaration) return;

    const currentState = removeDuplicates[declarationIndex] || false;
    const newState = !currentState;
    setRemoveDuplicates(prev => ({ ...prev, [declarationIndex]: newState }));
    
    if (hasDeclarationDocuments[declarationIndex]) {
      // Re-aggregate with new duplicate removal setting
      const updatedData = JSON.parse(JSON.stringify(editableData));
      const aggregated = aggregateDocumentosFromPartidas(declaration, newState);
      updatedData[declarationIndex].documentos = aggregated;
      setEditableData(updatedData);
      setIsDirty(true);
      isManualEditRefs.current[declarationIndex] = true; // Mark as manual edit to prevent auto-sync
    }
  };

  // Sync declaration documentos when partidas change (only if enabled)
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    editableData.forEach((declaration, declarationIndex) => {
      if (!declaration) return;

      // Skip sync if user just manually edited declaration documentos
      if (isManualEditRefs.current[declarationIndex]) {
        isManualEditRefs.current[declarationIndex] = false;
        return;
      }
      
      if (hasDeclarationDocuments[declarationIndex] && declaration.partidas) {
        // Create a signature of all partidas documentos
        const currentSignature = declaration.partidas
          .map(p => p.documentos ? JSON.stringify(p.documentos) : '[]')
          .join('|');
        
        const prevSignature = prevPartidasDocumentosRefs.current[declarationIndex];
        // Only update if the signature changed
        if (prevSignature !== currentSignature) {
          prevPartidasDocumentosRefs.current[declarationIndex] = currentSignature;
          const updatedData = JSON.parse(JSON.stringify(editableData));
          const removeDups = removeDuplicates[declarationIndex] || false;
          const aggregated = aggregateDocumentosFromPartidas(declaration, removeDups);
          updatedData[declarationIndex].documentos = aggregated;
          setEditableData(updatedData);
          setIsDirty(true);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData, hasDeclarationDocuments, removeDuplicates]);

  // Initialize toggle states based on declaration state
  useEffect(() => {
    if (!editableData || !Array.isArray(editableData)) return;

    const newStates = {};
    const newRemoveDups = {};
    editableData.forEach((declaration, index) => {
      if (declaration) {
        const hasDocs = declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0;
        newStates[index] = hasDocs;
        // Initialize removeDuplicates to false if not set
        if (!(index in removeDuplicates)) {
          newRemoveDups[index] = false;
        } else {
          newRemoveDups[index] = removeDuplicates[index];
        }
      }
    });
    setHasDeclarationDocuments(prev => ({ ...prev, ...newStates }));
    setRemoveDuplicates(prev => ({ ...prev, ...newRemoveDups }));
  }, [editableData?.length]);

  // Reset active item indices when items change
  useEffect(() => {
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      return;
    }
    
    // Use functional update to access current state without needing it in dependencies
    setActiveItemIndices(prev => {
      const newIndices = {};
      let hasChanges = false;
      
      editableData.forEach((declaration, declarationIndex) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          return; // Skip invalid declarations
        }
        
        // Validate partidas array
        const itemsCount = (declaration.partidas && Array.isArray(declaration.partidas)) 
          ? declaration.partidas.length 
          : 0;
        
        const currentIndex = prev[declarationIndex] ?? -1;
        if (currentIndex === -1) {
          // Preserve "All" selection - no change needed
          return;
        } else if (currentIndex >= itemsCount) {
          // Reset to "All" if out of bounds
          newIndices[declarationIndex] = -1;
          hasChanges = true;
        } else if (currentIndex >= 0 && currentIndex < itemsCount) {
          // Keep current if valid - no change needed
          return;
        }
      });
      
      // Only update if there are actual changes
      if (hasChanges) {
        return { ...prev, ...newIndices };
      }
      return prev; // Return same reference if no changes
    });
  }, [editableData]); // Only depend on editableData

  return (
    <div className="declaration-summary">
      <div className="">
        <div className={`${selectedOption === "split" && "sticky"} top-0 bg-white pt-4 w-full z-999`}>
          <div className=" gap-2  bg-white flex-col flex">
            <div className='justify-between gap-2 items-center flex bg-white'>
            <h2 className={`${isSummaryExpanded ? "text-lg" : "text-lg"} font-semibold`}>
              {getDeclarationSummaryTranslation('declarationSummary', isSpanish)}
            </h2>
            <div className="flex items-center gap-2">
              {/* Only show save/cancel buttons in split view - Main.js has them for non-split view */}
              {isDirty && selectedOption === "split" && (
                <div className="flex gap-2">
                  <button
                    className="bg-primary rounded-full flex items-center py-2 px-3 gap-2"
                    onClick={handleSaveClick}
                    title="Save Changes"
                  >
                    <Save className="size-4" />
                    <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                  </button>
                  <button
                    className="bg-red-50 rounded-full flex items-center py-2 px-3 gap-2"
                    onClick={handleCancelClick}
                    title="Cancel Changes"
                  >
                    <X className="stroke-red-500 size-4" />
                    <span className="text-red-500 text-xs">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                  </button>
                </div>
              )}
              {/* <button
                className={`flex items-center gap-2 py-2 px-3 rounded-full ${isSpanish ? "bg-default" : "bg-bgGray"}`}
                title="Language Toggle"
                onClick={() => { setIsSpanish(!isSpanish) }}
              >
                <Languages className={`size-4 ${isSpanish && "stroke-white"}`} />
                <p className={`text-xs ${isSpanish && "text-white"}`}>{isSpanish ? "ESP" : "ENG"}</p>
              </button> */}
            </div>
            </div>
            {selectedOption === "split" && validationErrors && validationErrors.length > 0 && (
              <div id="validation-errors" className="max-h-64 overflow-y-scroll">
                <ValidationErrors validationErrors={validationErrors} isSpanish={isSpanish} splitView={selectedOption === "split"}/>
              </div>
            )}
          </div>
        </div>

        {isSummaryExpanded && (
          <div className="mt-2">
            <div>
              <div className="grid grid-cols-2 gap-4 split-modal-header">
                <div className="form-group flex-1">
                  <label htmlFor="companyName">{getDeclarationSummaryTranslation('totalDeclarations', isSpanish)}</label>
                  <p className="font-bold text-4xl">{editableData.length || 0}</p>
                </div>
                <div className="form-group flex-1">
                  <label htmlFor="companyName">{getDeclarationSummaryTranslation('itemsCount', isSpanish)}</label>
                  <p className="font-bold text-4xl">{countTotalItems()}</p>
                </div>
              </div>
            </div>

            {/* Declaration Tabs Container */}
            {Array.isArray(editableData) && editableData.length > 0 && (
                <div className="border-ta pt-4 mt-4 overflow-x-auto">
                  <div className="flex gap-2 items-center pb-2">
                    {/* All Tab */}
                    <button
                      onClick={() => setActiveDeclarationIndex(-1)}
                      className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                        activeDeclarationIndex === -1
                          ? 'bg-secondary font-semibold px-4'
                          : 'pr-2'
                      }`}
                    >
                      {isSpanish ? 'Todos' : 'All'}
                    </button>
                    {/* Individual Declaration Tabs */}
                    {editableData && Array.isArray(editableData) && editableData.length > 0 && editableData
                      .filter((declaration, idx) => declaration !== null && declaration !== undefined && typeof declaration === 'object')
                      .map((declaration, declarationIndex) => (
                        <button
                          key={declarationIndex}
                          onClick={() => setActiveDeclarationIndex(declarationIndex)}
                          className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                            activeDeclarationIndex === declarationIndex
                              ? 'bg-secondary font-semibold'
                              : ''
                          }`}
                        >
                          {getDeclarationSummaryTranslation('export', isSpanish)} #{declarationIndex + 1}
                          {declaration?.expediente && ` - ${declaration.expediente}`}
                        </button>
                      ))}
                  </div>
                </div>
              )}

            {/* Show declarations based on active tab */}
            {editableData && Array.isArray(editableData) && editableData.length > 0 && (
              activeDeclarationIndex === -1 
                ? // Show all declarations
                  editableData
                    .filter((declaration, idx) => declaration !== null && declaration !== undefined && typeof declaration === 'object')
                    .map((declaration, declarationIndex) => (
                    <div key={declarationIndex} className={`mt-4 mb-2 border-t pt-4 first:border-t-0 first:pt-0`}>
                      <div className="flex items-center justify-between mb-4">
                      <h2 className={`text-4xl font-semibold split-header uppercase text-template-primary ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation('export', isSpanish)} #{declarationIndex + 1}
                      </h2>

                      {/* Declaration-Level Documents Toggle */}
                      <div className="">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 ">
                            <button
                              type="button"
                              onClick={() => handleToggleDeclarationDocuments(declarationIndex)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                hasDeclarationDocuments[declarationIndex]
                                  ? 'bg-primary text-white'
                                  : 'bg-secondary text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {hasDeclarationDocuments[declarationIndex] && (
                                <Check className="w-4 h-4" />
                              )}
                              <span>
                                {!isSpanish ? "Add Documents at Export Level" : "Agregar Documentos en Nivel de Exportación"} 
                              </span>
                            </button>
                            {(hasDeclarationDocuments[declarationIndex]) && (
                              <button
                                type="button"
                                onClick={() => handleToggleRemoveDuplicates(declarationIndex)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                  removeDuplicates[declarationIndex]
                                    ? 'bg-secondary'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {removeDuplicates[declarationIndex] && (
                                  <Check className="w-4 h-4" />
                                )}
                                <span>
                                  {!isSpanish ? "Remove Duplicates" : "Eliminar Duplicados"}
                                </span>
                              </button>
                            )}
                          </div>
                          {(hasDeclarationDocuments[declarationIndex]) && (
                            <span className="text-sm text-gray-600 ml-2">
                              {declaration.documentos?.length || 0} {!isSpanish ? "documents" : "documentos"}
                            </span>
                          )}
                        </div>
                      </div>
                      </div>

                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0 && (
                        <DeclarationDocumentsTable
                          editableData={editableData}
                          setEditableData={setEditableData}
                          setIsDirty={setIsDirty}
                          declarationIndex={declarationIndex}
                          isSpanish={isSpanish}
                          declaration={declaration}
                        />
                      )}

                      {/* Items (Partidas) */}
                      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0 text-3xl font-semibold uppercase">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                        <button
                          className="py-1 text-xs flex items-center gap-1"
                          onClick={() => {
                            // Validate editableData and declarationIndex
                            if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
                              console.warn('Cannot add item: editableData is invalid');
                              return;
                            }
                            
                            if (declarationIndex < 0 || declarationIndex >= editableData.length) {
                              console.warn(`Cannot add item: declarationIndex ${declarationIndex} out of bounds`);
                              return;
                            }
                            
                            const declaration = editableData[declarationIndex];
                            if (!declaration || typeof declaration !== 'object') {
                              console.warn(`Cannot add item: Invalid declaration at index ${declarationIndex}`);
                              return;
                            }
                            
                            try {
                              const updatedData = JSON.parse(JSON.stringify(editableData)); // Deep clone
                              if (!updatedData[declarationIndex].partidas) {
                                updatedData[declarationIndex].partidas = [];
                              }
                              updatedData[declarationIndex].partidas.push({
                                // Add default fields as needed
                                documentos: [],
                                embalajes: [],
                              });
                              setEditableData(updatedData);
                              setIsDirty(true);
                            } catch (error) {
                              console.error('Error adding item:', error);
                            }
                          }}
                        >
                          <Plus className={`size-4`} />
                          {getDeclarationSummaryTranslation('addItem', isSpanish)}
                        </button>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div className='split-table border-container p-4 rounded-md mt-4' key={itemIndex}>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                </div>
                              );
                        })()}
                      </div>
                    </div>
                  ))
                : // Show only active declaration
                  editableData[activeDeclarationIndex] && (() => {
                    const declaration = editableData[activeDeclarationIndex];
                    const declarationIndex = activeDeclarationIndex;
                    
                    return (
                    <div key={declarationIndex} className="mt-2 mb-2 border-t pt-4 first:border-t-0 first:pt-0">
                      <h2 className={`text-4xl font-semibold mb-4 split-header uppercase text-template-primary ${!declaration.expediente && 'text-red-600'}`}>
                        {getDeclarationSummaryTranslation('export', isSpanish)} #{declarationIndex + 1} -
                        <input
                         id={`expediente_${declarationIndex + 1}`}
                          value={declaration.expediente}
                          placeholder={
                            !declaration.expediente && !isSpanish
                              ? getDeclarationSummaryTranslation('enterDeclaration', false)
                              : getDeclarationSummaryTranslation('enterDeclaration', true)
                          }
                          onChange={(e) => handleChange(declarationIndex, 'expediente', e.target.value)}
                          className={`border-b border-gray-300 focus:border-blue-500 focus:outline-none px-2 ${!declaration.expediente && 'required-input'}`}
                          required
                        />
                      </h2>

                      {/* Declaration-Level Documents Toggle */}
                      <div className="mb-4 p-4 border rounded-md bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => handleToggleDeclarationDocuments(declarationIndex)}
                              className={`text-sm flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                                hasDeclarationDocuments[declarationIndex]
                                  ? 'bg-primary text-white'
                                  : 'bg-secondary text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {hasDeclarationDocuments[declarationIndex] && (
                                <Check className="w-4 h-4" />
                              )}
                              <span>
                                {!isSpanish ? "Add Documents at Export Level" : "Agregar Documentos en Nivel de Exportación"}
                              </span>
                            </button>
                            {(hasDeclarationDocuments[declarationIndex]) && (
                              <button
                                type="button"
                                onClick={() => handleToggleRemoveDuplicates(declarationIndex)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                                  removeDuplicates[declarationIndex]
                                    ? 'bg-primary text-white'
                                    : 'bg-secondary text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {removeDuplicates[declarationIndex] && (
                                  <Check className="w-4 h-4" />
                                )}
                                <span>
                                  {!isSpanish ? "Remove Duplicates" : "Eliminar Duplicados"}
                                </span>
                              </button>
                            )}
                          </div>
                          {(hasDeclarationDocuments[declarationIndex]) && (
                            <span className="text-sm text-gray-600">
                              {declaration.documentos?.length || 0} {!isSpanish ? "documents" : "documentos"}
                            </span>
                          )}
                        </div>
                      </div>

                      <DeclarationInformation 
                        handleChange={handleChange} 
                        declaration={declaration} 
                        declarationIndex={declarationIndex} 
                        isSpanish={isSpanish}
                        editableData={editableData}
                        setEditableData={setEditableData}
                        setIsDirty={setIsDirty}
                      />

                      {/* Declaration-Level Documents */}
                      {declaration.documentos && Array.isArray(declaration.documentos) && declaration.documentos.length > 0 && (
                        <DeclarationDocumentsTable
                          editableData={editableData}
                          setEditableData={setEditableData}
                          setIsDirty={setIsDirty}
                          declarationIndex={declarationIndex}
                          isSpanish={isSpanish}
                          declaration={declaration}
                        />
                      )}

                      {/* Items (Partidas) */}
                      <div className="form-group flex-1 mt-6 !mb-0 flex justify-between flex-row items-center">
                        <label htmlFor="partidas" className="!mb-0">{getDeclarationSummaryTranslation('items', isSpanish)} ({declaration.partidas?.length || 0})</label>
                        <button
                          className="py-1 text-xs flex items-center gap-1"
                          onClick={() => {
                            // Validate editableData and declarationIndex
                            if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
                              console.warn('Cannot add item: editableData is invalid');
                              return;
                            }
                            
                            if (declarationIndex < 0 || declarationIndex >= editableData.length) {
                              console.warn(`Cannot add item: declarationIndex ${declarationIndex} out of bounds`);
                              return;
                            }
                            
                            const declaration = editableData[declarationIndex];
                            if (!declaration || typeof declaration !== 'object') {
                              console.warn(`Cannot add item: Invalid declaration at index ${declarationIndex}`);
                              return;
                            }
                            
                            try {
                              const updatedData = JSON.parse(JSON.stringify(editableData)); // Deep clone
                              if (!updatedData[declarationIndex].partidas) {
                                updatedData[declarationIndex].partidas = [];
                              }
                              updatedData[declarationIndex].partidas.push({
                                // Add default fields as needed
                                documentos: [],
                                embalajes: [],
                              });
                              setEditableData(updatedData);
                              setIsDirty(true);
                            } catch (error) {
                              console.error('Error adding item:', error);
                            }
                          }}
                        >
                          <Plus className={`size-4`} />
                          {getDeclarationSummaryTranslation('addItem', isSpanish)}
                        </button>
                      </div>

                      {/* Item Tabs Container */}
                      {declaration.partidas && declaration.partidas.length > 0 && (
                        <div className="border-ta pt-4 mt-4 overflow-x-auto">
                          <div className="flex gap-2 items-center pb-2">
                            {/* All Tab */}
                            <button
                              onClick={() => setActiveItemIndex(declarationIndex, -1)}
                              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                                getActiveItemIndex(declarationIndex) === -1
                                  ? 'bg-secondary font-semibold px-4'
                                  : 'px-2'
                              }`}
                            >
                              {isSpanish ? 'Todos' : 'All'}
                            </button>
                            {/* Individual Item Tabs */}
                            {declaration.partidas.map((item, itemIndex) => (
                              <button
                                key={itemIndex}
                                onClick={() => setActiveItemIndex(declarationIndex, itemIndex)}
                                className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 px-4 whitespace-nowrap flex-shrink-0 ${
                                  getActiveItemIndex(declarationIndex) === itemIndex
                                    ? 'bg-secondary font-semibold'
                                    : ''
                                }`}
                              >
                                {getDeclarationSummaryTranslation('item', isSpanish)} #{itemIndex + 1}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show items based on active tab */}
                      <div className='flex flex-col'>
                        {declaration.partidas && (() => {
                          const currentActiveItemIndex = getActiveItemIndex(declarationIndex);
                          return currentActiveItemIndex === -1
                            ? // Show all items
                              declaration.partidas.map((item, itemIndex) => (
                          <div className='split-table border-container p-4 rounded-md mt-4' key={itemIndex}>
                            <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={item} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} />
                            <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                            <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={itemIndex} isSpanish={isSpanish} item={item} />
                          </div>
                              ))
                            : // Show only active item
                              declaration.partidas[currentActiveItemIndex] && (
                                <div className='split-table border-container p-4 rounded-md mt-4'>
                                  <ItemInformation editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} item={declaration.partidas[currentActiveItemIndex]} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} />
                                  <DocumentsTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                  <PackagesTable editableData={editableData} setEditableData={setEditableData} setIsDirty={setIsDirty} declarationIndex={declarationIndex} itemIndex={currentActiveItemIndex} isSpanish={isSpanish} item={declaration.partidas[currentActiveItemIndex]} />
                                </div>
                              );
                        })()}
                      </div>
                    </div>
                    );
                  })()
            )}
          </div>
        )}
      </div>
    </div>
  );
};